import { useEffect, useState } from "react";
import { Activity, ArrowDown, ArrowUp, Cpu, Gauge, LogOut, MemoryStick, Moon, Network, Sun, User as UserIcon } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/stores/auth";
import { fa } from "@/locales/fa";
import { Button } from "@/components/ui/button";
import { DashboardApi } from "@/lib/services";
import { formatBytes } from "@/lib/utils";
import { MobileNav } from "./MobileNav";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

type Theme = "dark" | "light";

function readTheme(): Theme {
  // Sync with the inline boot script in index.html. Whatever it set on the
  // <html> element is the truth — read it from there so React state matches
  // the actual rendered state on mount.
  if (typeof document === "undefined") return "dark";
  if (document.documentElement.classList.contains("light")) return "light";
  return "dark";
}

function applyTheme(t: Theme) {
  const html = document.documentElement;
  html.classList.toggle("dark", t === "dark");
  html.classList.toggle("light", t === "light");
  html.setAttribute("data-theme", t);
  try {
    localStorage.setItem("theme", t);
  } catch {
    /* private mode etc. — ignore */
  }
}

export function Header() {
  const { session, logout } = useAuth();
  const user = session?.user;
  const [theme, setTheme] = useState<Theme>(readTheme);
  const qc = useQueryClient();

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  /**
   * Centralized logout: clear the auth store AND the React Query cache, so
   * the next session can't briefly see the previous admin's data (cached
   * accounts, audit logs, etc.) before the queries refetch under the new
   * identity. AuthGate and WS hooks observe session changes separately and
   * tear down their own connections.
   */
  const handleLogout = () => {
    qc.clear();
    logout();
  };

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-background/70 backdrop-blur-xl px-4 md:px-5">
      {/* Hamburger opens the mobile nav drawer; the desktop sidebar carries
          navigation at md+ so this collapses away there. */}
      <MobileNav />
      {/* In RTL flex, the first child sits at the visual right end. We
          want the live host-stats chip pinned to the right edge of the
          header, so it goes here before the flex-1 spacer that pushes
          the theme + user controls to the left. */}
      <HostStatsChip />
      <div className="flex-1" />

      <Button
        variant="ghost"
        size="icon"
        onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
        aria-label={theme === "dark" ? "حالت روشن" : "حالت تاریک"}
        className="text-muted-foreground hover:text-foreground"
      >
        {theme === "dark" ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            <div className="grid place-items-center size-7 rounded-full bg-muted text-foreground">
              <UserIcon className="size-[14px]" />
            </div>
            <span className="text-sm font-medium">{user?.fullName ?? user?.username ?? "ادمین"}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span className="text-sm text-foreground">{user?.fullName}</span>
            <span className="text-xs text-muted-foreground lat-num" dir="ltr">
              {user?.username ? `@${user.username}` : user?.email}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={handleLogout}>
            <LogOut className="size-4" />
            <span>{fa.nav.logout}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

/**
 * Live host-stats chip for the right edge of the header. Shows CPU%, RAM%
 * and current uplink rx/tx for the panel host. Refreshes every 5 s, which
 * matches the backend sampler cadence — there's no point polling faster.
 *
 * Hides itself completely while the API returns 503 (sampler hasn't taken
 * two snapshots yet on a fresh boot) or any other error, so a broken metric
 * source doesn't paint zeros across the header.
 */
function HostStatsChip() {
  const q = useQuery({
    queryKey: ["host-stats"],
    queryFn: () => DashboardApi.hostStats(),
    refetchInterval: 5000,
    retry: false,
  });
  // Internet quota lives behind its own endpoint — surface it in the
  // header so the operator sees remaining reserve at a glance from any
  // page, not just the dashboard. Hidden when no data (sampler not run
  // yet, session expired, reserveGb=0/missing — all the same UX).
  const tci = useQuery({
    queryKey: ["header-tci"],
    queryFn: () => DashboardApi.tciUsage(),
    refetchInterval: 10 * 60 * 1000,
    retry: false,
  });
  if (q.isError || !q.data) return null;
  const s = q.data;
  // Bandwidth in bit-based units to match nload and what the ISP
  // advertises (e.g. "1 Gbps line"). Suffix dropped (no /ث) per the
  // operator's preference — the chip's context already implies "per
  // second". Decimal scale (not binary) because bps SI convention.
  const fmtBitsPerSec = (bps: number | null): string => {
    if (bps == null) return "—";
    if (bps < 1000) return `${Math.round(bps)} بیت`;
    if (bps < 1_000_000) return `${(bps / 1000).toFixed(1)} کیلوبیت`;
    if (bps < 1_000_000_000) return `${(bps / 1_000_000).toFixed(1)} مگابیت`;
    return `${(bps / 1_000_000_000).toFixed(2)} گیگابیت`;
  };
  const cpu = s.cpuPercent;
  const cpuTone =
    cpu == null ? "" : cpu > 85 ? "text-destructive" : cpu > 60 ? "text-warning" : "text-foreground";
  const memTone =
    s.memPercent > 85 ? "text-destructive" : s.memPercent > 60 ? "text-warning" : "text-foreground";
  // Load tone is normalised against core count: >1× cores = saturated.
  // We only show 1-min load to keep the chip compact; tooltip carries
  // the 5 / 15-min figures for operators who want trend context.
  const cores = s.numCpus ?? 1;
  const loadPct = s.load1 != null ? (s.load1 / cores) * 100 : null;
  const loadTone =
    loadPct == null ? "" : loadPct > 100 ? "text-destructive" : loadPct > 70 ? "text-warning" : "text-foreground";
  const loadTitle =
    s.load1 != null
      ? `Load: ${s.load1.toFixed(2)} / ${s.load5?.toFixed(2) ?? "—"} / ${s.load15?.toFixed(2) ?? "—"} (${cores} core)`
      : "load average در دسترس نیست";
  const t = tci.data;
  // Only render the quota chip when the scraper has a usable remaining-
  // service value. We use `trafficRemainingGb` (the headline gauge from
  // the TCI panel) — matches the dashboard card. `reserveGb` is the
  // separately-counted extra and lives in its own tooltip below.
  const showTci = !!t && typeof t.trafficRemainingGb === "number" && t.trafficRemainingGb > 0;
  return (
    <div
      className="hidden md:flex items-center gap-3 text-[11px] text-muted-foreground"
      title={`نمونه‌گیری زنده هر ۵ ثانیه — ${s.ifaceName}`}
    >
      <span className="flex items-center gap-1.5">
        <Cpu className="size-3.5" />
        <span className={`tabular lat-num ${cpuTone}`}>
          {cpu == null ? "—" : `${Math.round(cpu)}٪`}
        </span>
      </span>
      <span className="flex items-center gap-1.5" title={`${formatBytes(s.memUsedBytes)} / ${formatBytes(s.memTotalBytes)}`}>
        <MemoryStick className="size-3.5" />
        <span className={`tabular lat-num ${memTone}`}>{Math.round(s.memPercent)}٪</span>
      </span>
      <span className="flex items-center gap-1.5" title={loadTitle}>
        <Gauge className="size-3.5" />
        <span className={`tabular lat-num ${loadTone}`}>
          {s.load1 == null ? "—" : s.load1.toFixed(2)}
        </span>
      </span>
      <span className="flex items-center gap-1.5" title={`${s.ifaceName} — دانلود`}>
        <ArrowDown className="size-3.5 text-success/80" />
        <span className="tabular lat-num text-foreground">{fmtBitsPerSec(s.rxBps)}</span>
      </span>
      <span className="flex items-center gap-1.5" title={`${s.ifaceName} — آپلود`}>
        <ArrowUp className="size-3.5 text-primary/80" />
        <span className="tabular lat-num text-foreground">{fmtBitsPerSec(s.txBps)}</span>
      </span>
      {showTci && (
        <span
          className="flex items-center gap-1.5 border-s border-border/60 ps-3"
          title={
            t!.reserveGb && t!.reserveGb > 0
              ? `ترافیک باقی‌مانده · ${formatBytes(t!.reserveGb * 1024 ** 3)} رزرو`
              : "ترافیک باقی‌مانده"
          }
        >
          <Network className="size-3.5 text-primary/80" />
          <span className="tabular lat-num text-foreground">
            {formatBytes(t!.trafficRemainingGb * 1024 ** 3)}
          </span>
        </span>
      )}
      <Activity className="size-3 text-muted-foreground/50 animate-pulse" />
    </div>
  );
}
